import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAreaExpansion } from "./AreaExpansionContext";
import { useAreaLogic } from "../../../hooks/useAreaLogic";
import { ObjectType } from "../../../hooks/useObjectType";
import { AreaRow, INDENT_WIDTH } from "../../../helper/area-tree";
import AreaItem from "./AreaItem.web";

type Props = {
  row: AreaRow;
  projectId: string;
  objectId: string;
  objectType: ObjectType | null;
  cracksCount: number;
  isLoadingCracks: boolean;
  indentLevel: number;
};

/**
 * A single row of the flat area list. The tree shape lives in the flattened
 * rows, so this renders one area and nothing below it.
 */
export default function DisplayArea({
  row,
  projectId,
  objectId,
  objectType,
  cracksCount,
  isLoadingCracks,
  indentLevel,
}: Props) {
  const { id, area, level, rootAreaType, hasChildren } = row;
  const title = area.get("title") ?? "";

  const { canAddSubareas, canAddCracks } = useAreaLogic(
    objectType,
    level,
    rootAreaType
  );

  const { toggle } = useAreaExpansion();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    paddingLeft: (indentLevel - 1) * INDENT_WIDTH,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <AreaItem
        areaId={id}
        title={title}
        projectId={projectId}
        objectId={objectId}
        level={level}
        cracksCount={cracksCount}
        canAddCracks={canAddCracks}
        isLoadingCracks={isLoadingCracks}
        rootAreaTypeForChilden={rootAreaType}
        attributes={attributes}
        listeners={listeners}
        canCollapse={hasChildren || canAddSubareas}
        isExpanded={row.isExpanded}
        onToggleExpand={() => toggle(id)}
      />
    </div>
  );
}
